import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';

import { BaseCrud, FormSection, FormSectionColumn } from 'react/containers/layout/Crud';
import {
  TextField,
  LookupField,
  AddAttributesField,
  IntegerField
} from 'react/components/fields';

const entitiesPropertiesList = [
  'Equipment.key',
  'Equipment.description',
  'Equipment.equipmentType.key',
  'Equipment.equipmentType.description',
  'Equipment.equipmentType.weight',
  'Equipment.equipmentType.height',
  'Equipment.equipmentType.size1',
  'Equipment.equipmentType.size2',
  'Equipment.equipmentType.size3',
  'Stop.location.key',
  'Stop.location.accountType.key',
  'Stop.location.accountType.description',
  'Stop.location.locationType.key',
  'Stop.location.locationType.description',
  'Stop.location.timeWindowType.key',
  'Stop.location.timeWindowType.description',
  'Stop.location.serviceTimeType.key',
  'Stop.location.serviceTimeType.description',
  'Stop.location.description',
  'Stop.location.addressLine1',
  'Stop.location.addressLine2',
  'Stop.location.city',
  'Stop.location.state',
  'Stop.location.zipCode',
  'Stop.location.district',
  'Stop.location.longitude',
  'Stop.location.latitude',
  'Stop.location.phone1',
  'Stop.location.phone1Type',
  'Stop.location.phone2',
  'Stop.location.phone2Type',
  'Stop.location.email',
  'Stop.location.deliveryInstructions',
  'Stop.key',
  'Stop.baseLineSequenceNum',
  'Stop.plannedSequenceNum',
  'Stop.actualSequenceNum',
  'Stop.stopType.description',
  'Stop.baseLineArrival',
  'Stop.plannedArrival',
  'Stop.projectedArrival',
  'Stop.actualArrival',
  'Stop.baseLineService',
  'Stop.plannedService',
  'Stop.projectedService',
  'Stop.actualService',
  'Stop.baseLineDeparture',
  'Stop.plannedDeparture',
  'Stop.projectedDeparture',
  'Stop.actualDeparture',
  'Stop.baseLineDistance',
  'Stop.plannedDistance',
  'Stop.projectedDistance',
  'Stop.actualDistance',
  'Stop.baseLineSize1',
  'Stop.baseLineSize2',
  'Stop.baseLineSize3',
  'Stop.plannedSize1',
  'Stop.plannedSize2',
  'Stop.plannedSize3',
  'Stop.actualSize1',
  'Stop.actualSize2',
  'Stop.actualSize3',
  'Stop.plannedPickupSize1',
  'Stop.plannedPickupSize2',
  'Stop.plannedPickupSize3',
  'Stop.baseLinePickupSize1',
  'Stop.baseLinePickupSize2',
  'Stop.baseLinePickupSize3',
  'Stop.actualPickupSize1',
  'Stop.actualPickupSize2',
  'Stop.actualPickupSize3',
  'Stop.damagedSize1',
  'Stop.damagedSize2',
  'Stop.damagedSize3',
  'Stop.instructions',
  'Stop.deliveryReasonCode.description',
  'Stop.overReasonCode.description',
  'Stop.shortReasonCode.description',
  'Stop.damagedReasonCode.description',
  'Stop.pickupReasonCode.description',
  'Stop.cancelCode.description',
  'Stop.undeliverableCode.description',
  'Stop.arrivalLatitude',
  'Stop.arrivalLongitude',
  'Stop.arrivalAccuracyMeters',
  'Stop.arrivedInTimeWindow',
  'Stop.serviceLatitude',
  'Stop.serviceAccuracyMeters',
  'Stop.serviceLongitude',
  'Stop.longitude',
  'Stop.latitude',
  'Stop.ordersInfo',
  'Route.date',
  'Route.key',
  'Route.description',
  'Route.origin.key',
  'Route.origin.description',
  'Route.origLatitude',
  'Route.origLongitude',
  'Route.destination.key',
  'Route.destination.description',
  'Route.destLatitude',
  'Route.destLongitude',
  'Route.baseLineArrival',
  'Route.baseLineDeparture',
  'Route.plannedArrival',
  'Route.plannedDeparture',
  'Route.actualArrival',
  'Route.actualDeparture',
  'Route.baseLineDistance',
  'Route.plannedDistance',
  'Route.actualDistance',
  'Route.baseLineComplete',
  'Route.plannedComplete',
  'Route.actualComplete',
  'Route.baseLineCost',
  'Route.plannedCost',
  'Route.actualCost',
  'Route.baseLineStart',
  'Route.plannedStart',
  'Route.actualStart',
  'Route.hasHelper',
  'Route.driversName',
  'Route.totalStops',
  'Route.canceledStops',
  'Route.undeliveredStops',
  'Route.redeliveredStops',
  'Route.actualTravelTimeMinutes',
  'Route.plannedTravelTimeMinutes',
  'Route.baselineTravelTimeMinutes',
  'Route.hasPicture',
  'Driver.key',
  'Driver.name',
  'Driver.login',
  'HoursOfService.onDuty'
];

const isFilledLessOneOfThisFields = (field1, field2) => (!field1 && !field2);

const renderGeneralSection = ({ id }) => {
  const isEdit = !!id;
  return (
    <FormSection title="TicketType.section.general.title" className="generalSection">
      <FormSectionColumn>
        <LookupField
          label="TicketType.field.organization"
          name="organization"
          lookupEntity="Organization"
          lookupEntityColumn="description"
          required
          disabled={isEdit}
        />
        <TextField
          label="TicketType.field.key"
          name="key"
          disabled={isEdit}
        />
        <TextField
          label="TicketType.field.description"
          name="description"
          required
        />
        <IntegerField
          label="TicketType.field.dueDateDays"
          name="dueDateDays"
          min={1}
          required
        />
      </FormSectionColumn>
    </FormSection>
  );
};

const renderPredeterminedMessage = ({ entity, isGrid }) => (
  <FormSection title="TicketType.section.predeterminedMessage.title" className="predeterminedMessage">
    <FormSectionColumn>
      <AddAttributesField
        label="TicketType.field.standardTicketTitle"
        name="standardTicketTitle"
        required
        entity={entity}
        isGrid={isGrid}
        entitiesPropertiesList={entitiesPropertiesList}
      />
      <AddAttributesField
        label="TicketType.field.standardTicketDescription"
        name="standardTicketDescription"
        required
        entity={entity}
        isGrid={isGrid}
        entitiesPropertiesList={entitiesPropertiesList}
        isTextArea
      />
    </FormSectionColumn>
  </FormSection>
);

const renderVisualNotifications = ({ standardUsersCall, standardGroupsCall }) => (
  <FormSection title="TicketType.section.visualNotifications.title" className="visualNotifications">
    <FormSectionColumn>
      <LookupField
        label="TicketType.field.standardUsersCall"
        name="standardUsersCall"
        lookupEntity="Users"
        lookupEntityColumn="name"
        multiple
        required={isFilledLessOneOfThisFields(standardUsersCall, standardGroupsCall)}
      />
      <LookupField
        label="TicketType.field.standardGroupsCall"
        name="standardGroupsCall"
        lookupEntity="Group"
        lookupEntityColumn="name"
        multiple
        required={isFilledLessOneOfThisFields(standardUsersCall, standardGroupsCall)}
      />
    </FormSectionColumn>
  </FormSection>
);

const TicketType = (props) => (
  <BaseCrud updateMethod="PATCH" tabName="TicketType.title" {...props}>
    {renderGeneralSection(props)}
    {renderPredeterminedMessage(props)}
    {renderVisualNotifications(props)}
  </BaseCrud>
);

renderPredeterminedMessage.propTypes = {
  entity: PropTypes.string.isRequired,
  isGrid: PropTypes.bool.isRequired
};

renderVisualNotifications.propTypes = {
  standardUsersCall: PropTypes.string.isRequired,
  standardGroupsCall: PropTypes.bool.isRequired
};

renderGeneralSection.propTypes = {
  id: PropTypes.string.isRequired
};

const selector = formValueSelector('TicketType');
const mapStateToProps = (state) => ({
  standardUsersCall: selector(state, 'standardUsersCall'),
  standardGroupsCall: selector(state, 'standardGroupsCall')
});

export default connect(mapStateToProps)(TicketType);
